import { Box, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@material-ui/core';
import { Pagination, PaginationItem, PaginationRenderItemParams } from '@material-ui/lab';
import { ReactComponent as ArrowDown } from 'assets/arrow-down.svg';
import { ReactComponent as ArrowUp } from 'assets/arrow-up.svg';
import { default as classnames, default as classNames } from 'classnames';
import { Loader } from 'components';
import SGSInput from 'components/SGSInput';
import { matchSorter } from 'match-sorter';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Cell, ColumnInstance, HeaderGroup, Row, useFilters, useFlexLayout, usePagination, useSortBy, useTable } from 'react-table';
//import { filtersChanged, pageChanged, pageSizeChanged, sortByChanged } from '../../features/affiliates/searchParamsSlice';
import styles from './SGSTable.module.scss';

export type TableSortParams = {
    column: string;
    order: 'ASC' | 'DESC';
};

type Props = {
    columns: any;
    data: any;
    onRowClick?: Function;
    disableFooter?: boolean;
    loading?: boolean;
    noRowsLabel?: string;
    globalFilter?: any;
    pageCount?: number;
    totalResults?: number;
    filtersChanged: Function;
    pageChanged: Function;
    pageSizeChanged: Function;
    sortByChanged: Function;
    searchParams: any;
};

// Define a default UI for filtering
function DefaultColumnFilter({ column: { filterValue, setFilter, Header } }) {
    return (
        <SGSInput
            value={filterValue || ''}
            onChange={(e) => {
                setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
            }}
            placeholder={Header}
        ></SGSInput>
    );
}

function fuzzyTextFilterFn(rows, id, filterValue) {
    return matchSorter(rows, filterValue, { keys: [(row: any) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

const SGSTableCtrl: React.FC<Props> = ({
    columns,
    data,
    onRowClick,
    disableFooter,
    loading,
    noRowsLabel,
    globalFilter,
    pageCount: totalPages,
    totalResults = null,
    filtersChanged,
    pageChanged,
    pageSizeChanged,
    sortByChanged,
    searchParams,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //const searchParams = JSON.parse(useSelector((state: any) => state.searchParams.value));
    searchParams = JSON.parse(searchParams);
    const initialState = { pageIndex: searchParams.page - 1, pageSize: searchParams.pageSize, sortBy: searchParams.sortBy }; //useSelector((state: any) => state.searchParams.value);

    const filterTypes = React.useMemo(
        () => ({
            // Add a new fuzzyTextFilterFn filter type.
            fuzzyText: fuzzyTextFilterFn,
            // Or, override the default text filter to use
            // "startWith"
            text: (rows, id, filterValue) => {
                return rows.filter((row) => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase()) : true;
                });
            },
        }),
        [],
    );

    const defaultColumn: any = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        [],
    );
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        footerGroups,
        prepareRow,
        page,
        pageCount,
        state: { pageIndex, sortBy, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            initialState,
            manualPagination: true,
            manualSortBy: true,
            autoResetPage: false,
            pageCount: totalPages,
        },
        useFilters,
        useFlexLayout,
        useSortBy,
        usePagination,
    );

    useEffect(() => {
        dispatch(pageChanged(pageIndex + 1));
    }, [pageIndex, dispatch]);

    useEffect(() => {
        dispatch(filtersChanged(globalFilter));
    }, [globalFilter, dispatch]);

    useEffect(() => {
        dispatch(pageSizeChanged(pageSize));
    }, [pageSize, dispatch]);

    useEffect(() => {
        dispatch(sortByChanged(sortBy));
    }, [sortBy, dispatch]);

    return (
        <Box maxWidth="100%" width="100%">
            <TableContainer className={styles.tableWrap}>
                <Table {...getTableProps()} className={styles.table}>
                    <TableHead>
                        {headerGroups.map((headerGroup: HeaderGroup) => (
                            <TableRow {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column: ColumnInstance) => (
                                    // Add the sorting props to control sorting. For this example
                                    // we can add them into the header props
                                    <TableCell
                                        classes={{ root: styles.tableCell__head }}
                                        //     ? column.getHeaderProps()
                                        {...column.getHeaderProps({
                                            className: column.headerClassName,
                                        })}
                                    >
                                        <Box
                                            {...column.getSortByToggleProps({ title: '' })}
                                            className={`${styles.tableCell__head__content} ${column.Header ? 'justify-content-between' : 'justify-content-center'}`}
                                        >
                                            {column.render('Header')}
                                            {/* Add a sort direction indicator */}
                                            {column.id !== 'selection' && column.canSort ? (
                                                <Box component="span" ml={0.5} className="text-nowrap">
                                                    <ArrowUp className={classnames(styles.table__sort, column.isSorted && !column.isSortedDesc && styles.table__sort__active)} />
                                                    <ArrowDown className={classnames(styles.table__sort, column.isSorted && column.isSortedDesc && styles.table__sort__active)}></ArrowDown>
                                                </Box>
                                            ) : null}
                                        </Box>
                                        {column.canFilter && <div>{column.render('Filter')}</div>}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" className="justify-content-center" classes={{ body: styles.tableCell__body }}>
                                    <Loader loading={loading} small></Loader>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {data.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center" className="justify-content-center" classes={{ body: styles.tableCell__body }}>
                                            {noRowsLabel || t('components.datagrid.noRows')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <>
                                        {page.map((row: Row, _i) => {
                                            prepareRow(row);
                                            return (
                                                <TableRow
                                                    hover={!!onRowClick}
                                                    {...row.getRowProps()}
                                                    onClick={() => (onRowClick ? onRowClick(row) : {})}
                                                    className={styles.tableRow__body}
                                                    classes={{ hover: styles.tableRowHover }}
                                                >
                                                    {row.cells.map((cell: Cell) => {
                                                        return (
                                                            <TableCell
                                                                {...cell.getCellProps({
                                                                    className: classnames(
                                                                        styles.tableCell__body,
                                                                        typeof cell.column.className === 'function' ? cell.column.className(cell) : cell.column.className,
                                                                    ),
                                                                })}
                                                            >
                                                                {cell.render('Cell')}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        })}
                                    </>
                                )}
                            </>
                        )}
                    </TableBody>
                    {!disableFooter && (
                        <TableFooter>
                            {footerGroups.map((group) => (
                                <TableRow {...group.getFooterGroupProps()}>
                                    {group.headers.map((column: ColumnInstance) => (
                                        <TableCell {...column.getFooterProps()} classes={{ footer: styles.tableCell__footer }}>
                                            {column.render('Footer')}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
            {pageCount > 1 && (
                <>
                    <Box className="row">
                        <Box className={classNames('col-3 pt-2 text-center ', styles.totalResult)}>
                            {/* {totalResults && totalResults >= 2
                                ? new Intl.NumberFormat('fr-FR').format(totalResults) + ' résultats'
                                : ''}
                            {totalResults && totalResults <= 1
                                ? new Intl.NumberFormat('fr-FR').format(totalResults) + ' résultat'
                                : ''} */}
                        </Box>
                        <Box className="col-6">
                            <Pagination
                                count={pageCount}
                                page={searchParams.page}
                                boundaryCount={3}
                                siblingCount={2}
                                shape="rounded"
                                variant="outlined"
                                onChange={(_event, value) => {
                                    dispatch(pageChanged(value));
                                }}
                                classes={{ root: styles.pagination, ul: styles.list }}
                                renderItem={(item: PaginationRenderItemParams) => (
                                    <PaginationItem
                                        {...item}
                                        classes={{
                                            root: styles.item,
                                            page: styles[item.type],
                                            ellipsis: styles.ellipsis,
                                            selected: styles.selected,
                                            icon: styles.icon,
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <Box className="col-3"></Box>
                    </Box>
                    <Box className={classNames('col-12 pt-2 text-center ', styles.totalResult)}>
                        {totalResults && totalResults >= 2 ? new Intl.NumberFormat('fr-FR').format(totalResults) + ' résultats' : ''}
                        {totalResults && totalResults <= 1 ? new Intl.NumberFormat('fr-FR').format(totalResults) + ' résultat' : ''}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default SGSTableCtrl;
